import {
  ManyIncludedQuoteResourceUnion,
  SingleIncludedQuoteResourceUnion,
} from 'api-client'
import { AxiosResponse } from 'axios'

import { OrderFilter } from '../filters/orders.filters'
import { QuoteFilter, QuoteFilters } from '../filters/quotes.filters'
import { FilterMap, mapFiltersToAPIQuery } from '../filters/utils'
import { QuoteModel } from '../model/quote.model'
import {
  TransformedQuote,
  transformQuote,
  transformQuotes,
} from '../transforms/quote.transforms'
import { deserialize } from '../utils/deserialize'
import { extractFilenameFromHeaders } from './orders'
import { request } from './request'

/**
 *  Any transforms that need to be done to the filter query
 *  before it gets passed along to the API should go here
 */
const formatFilters = ({ ...rest }: FilterMap<QuoteFilters>) => ({
  ...rest,
})

interface FetchQuotesParams {
  apiBaseUrl: string
  customerContextId: string | undefined
  filters: FilterMap<typeof OrderFilter>
  include?: string
  limit?: number
  offset?: number
  salesGroupId: string | undefined
}

interface FetchQuotesWithAcceptParams extends FetchQuotesParams {
  acceptType: string | undefined
}

const fetchQuotes = <T>({
  acceptType,
  apiBaseUrl,
  customerContextId,
  filters,
  include,
  limit,
  offset,
  salesGroupId,
}: FetchQuotesWithAcceptParams): Promise<AxiosResponse<T>> =>
  request<T>({
    headers: {
      Accept: acceptType,
    },
    method: 'GET',
    params: {
      'filter[customerContext.id.equals]': customerContextId,
      'filter[customerContext.salesGroup.id.equals]': salesGroupId,
      include,
      'page[number]': offset,
      'page[size]': limit,
      ...mapFiltersToAPIQuery(formatFilters(filters), QuoteFilter),
    },
    url: `${apiBaseUrl}/api/quotes`,
  })

export const fetchQuotesCsv = async (
  params: FetchQuotesParams,
): Promise<{
  error?: boolean
  fileName: string
  quotes: string
}> => {
  try {
    const response = await fetchQuotes<string>({
      ...params,
      acceptType: 'text/csv',
    })

    return {
      fileName: extractFilenameFromHeaders(response.headers),
      quotes: response.data,
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed fetching quotes', e)
    return {
      error: true,
      fileName: '',
      quotes: '',
    }
  }
}

export type FetchQuoteJsonResponse = {
  error?: boolean
  quotes: TransformedQuote[]
  total: number
  totalPages?: number
}

export const fetchQuotesJson = async (
  params: FetchQuotesParams,
): Promise<FetchQuoteJsonResponse> => {
  try {
    const response = await fetchQuotes<ManyIncludedQuoteResourceUnion>({
      ...params,
      acceptType: 'application/json',
    })

    const quotes = deserialize<QuoteModel[]>(response?.data || {})
    const total = response.data.meta.count | 0
    return {
      quotes: transformQuotes(quotes),
      total,
      totalPages: params.limit ? Math.ceil(total / params.limit) : undefined,
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed fetching quotes', e)
    return {
      error: true,
      quotes: [],
      total: 0,
    }
  }
}

interface FetchQuoteDetailsParams {
  apiBaseUrl: string
  customerContextId: string | undefined
  quoteId: string
  salesGroupId?: string
}

export const fetchQuoteDetails = async ({
  apiBaseUrl,
  customerContextId,
  quoteId,
  salesGroupId,
}: FetchQuoteDetailsParams): Promise<TransformedQuote> => {
  const response = await request<SingleIncludedQuoteResourceUnion>({
    headers: {
      Accept: 'application/json',
    },
    method: 'GET',
    params: {
      'filter[customerContext.id.equals]': customerContextId,
      'filter[customerContext.salesGroup.id.equals]': salesGroupId,
      include:
        'soldToQuote,shipToQuote,billToQuote,quoteLines.product.customerContextProducts,customerContext',
    },
    url: `${apiBaseUrl}/api/quotes/${quoteId}`,
  })

  const deserializedData = deserialize<QuoteModel>(response?.data)

  return transformQuote(deserializedData) as TransformedQuote
}
